/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import SaveIcon from "@mui/icons-material/Save";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { BreadCrumbsContext } from "../../Context/breadCrumbsContext";
import { getData } from "../../Services/getData";
import { postData } from "../../Services/postData";
import { UrlEnum } from "../../Utils/Utils";
import { Vocabulary } from "../../Utils/Vocabulary";
import { breadCrumbsEnum } from "../../Utils/Constants";
import StatusOrder from "../Orders/StatusOrder";
import OrderShipping from "../Orders/OrderShipping";
import DeviceRepairModal from "../DeviceRepairs/DeviceRepairModal";
import Modal from "../../Components/Modal";
import SendEmailModal from "../Orders/SendEmailModal";
import theme from "../../Theme/Theme";
import style from "../Orders/Orders.module.scss";

const styles = {
  color: "black",
  boxShadow: "none",
  width: "calc(100% - 260px)",
};

export default function RepairOrder() {
  const location = useLocation();
  const breadCrumbsContext = useContext(BreadCrumbsContext);
  const notifyError = (msg: string) => toast.error(msg);
  const notifySuccess = (msg: string) => toast.success(msg);

  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [deviceRepairs, setDeviceRepairs] = useState<any[]>([]);
  const [openRepairModal, setOpenRepairModal] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState<any>(null);
  const [sendEmailModal, setSendEmailModal] = useState(false);
  const [pvPreview, setPvPreview] = useState<{
    open: boolean;
    url: string | null;
    filename: string;
  }>({ open: false, url: null, filename: "" });

  useEffect(() => {
    let id = location.pathname;
    id = id.replace("/repairOrders/", "");
    if (id) {
      getOrder(id);
    }
    getData(`${UrlEnum.deviceRepairs}`)
      .then((res) => setDeviceRepairs(res.data || []))
      .catch((_e) => undefined);
  }, []);

  function getOrder(id: string) {
    setLoading(true);
    getData(`${UrlEnum.repairOrders}/${id}`)
      .then((res) => {
        const [o] = res.data;
        breadCrumbsContext.setBreadCrumbs([
          `${breadCrumbsEnum.repairOrders}`,
          `${o.orderNumber}`,
        ]);
        if (o.repair?.spareParts) {
          o.repair.spareParts = o.repair.spareParts.map((p: any, i: number) => ({
            ...p,
            _key: p._key || `${Date.now()}-${i}`,
          }));
        }
        setOrder(o);
      })
      .catch(() => notifyError(Vocabulary.getError))
      .finally(() => setLoading(false));
  }

  function handleChangeStatus(
    id: string | null | undefined,
    value: string | undefined
  ) {
    const newStatus = { ...order.status, order: value };
    setOrder({ ...order, status: newStatus, statusId: id });
  }

  function handleChangeClientInfo(key: string, value: string) {
    setOrder({ ...order, clientInfo: { ...order.clientInfo, [key]: value } });
  }

  function handleChangeShipping(shipping: any) {
    setOrder({ ...order, shipping });
  }

  function handleChangeOrder(newOrder: any) {
    setOrder(newOrder);
  }

  function computeTotal(spareParts: any[], labourPrice: number) {
    const partsTotal = spareParts.reduce(
      (sum: number, p: any) => sum + (Number(p.price) || 0),
      0
    );
    return partsTotal + (Number(labourPrice) || 0);
  }

  function handleRepairModalClose(savedRepair?: any) {
    setOpenRepairModal(false);
    if (savedRepair) {
      setDeviceRepairs((prev) => {
        const exists = prev.some((r) => String(r._id) === String(savedRepair._id));
        return exists ? prev : [...prev, savedRepair];
      });
      handleLoadRepairTemplate(null, savedRepair);
    }
  }

  function handleLoadRepairTemplate(_: any, repair: any) {
    if (!repair) return;
    const spareParts = (repair.spareParts || []).map((p: any, i: number) => ({
      ...p,
      _key: `${Date.now()}-${i}`,
    }));
    const updated = {
      ...repair,
      spareParts,
      totalPrice: computeTotal(spareParts, repair.labourPrice || 0),
    };
    setOrder({ ...order, repair: updated });
  }

  function handleChangeRepairField(key: string, value: any) {
    const updated = { ...order.repair, [key]: value };
    if (key === "labourPrice") {
      updated.totalPrice = computeTotal(updated.spareParts || [], value);
    }
    setOrder({ ...order, repair: updated });
  }

  function handleChangeSparePartField(index: number, key: string, value: any) {
    const parts = [...(order.repair?.spareParts || [])];
    parts[index] = { ...parts[index], [key]: value };
    const updated = { ...order.repair, spareParts: parts };
    updated.totalPrice = computeTotal(parts, updated.labourPrice || 0);
    setOrder({ ...order, repair: updated });
  }

  function handleAddSparePart() {
    const parts = [
      ...(order.repair?.spareParts || []),
      { name: "", price: 0, _key: `${Date.now()}` },
    ];
    const updated = { ...order.repair, spareParts: parts };
    updated.totalPrice = computeTotal(parts, updated.labourPrice || 0);
    setOrder({ ...order, repair: updated });
  }

  function handleRemoveSparePart(index: number) {
    const parts = (order.repair?.spareParts || []).filter(
      (_: any, i: number) => i !== index
    );
    const updated = { ...order.repair, spareParts: parts };
    updated.totalPrice = computeTotal(parts, updated.labourPrice || 0);
    setOrder({ ...order, repair: updated });
  }

  async function saveOrder(sendEmail?: boolean, emailBody?: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!order) return reject();
      setLoading(true);
      const repair = order.repair
        ? {
            ...order.repair,
            spareParts: (order.repair.spareParts || []).map(
              ({ _key, ...p }: any) => p
            ),
          }
        : null;
      const body: any = {
        clientInfo: order.clientInfo,
        shipping: order.shipping,
        repair,
        status: order.status,
        statusId: order.statusId,
        orderNumber: order.orderNumber,
        sendEmail: !!sendEmail,
        ...(emailBody || {}),
      };
      postData(`${UrlEnum.repairOrders}/${order._id}`, body)
        .then((res) => {
          notifySuccess(Vocabulary.saveSuccess);
          resolve(res);
        })
        .catch((err) => {
          notifyError(Vocabulary.saveError);
          reject(err);
        })
        .finally(() => setLoading(false));
    });
  }

  async function getEmailTemplate(statusId: string) {
    await getData(`${UrlEnum.emailTemplates}/${statusId}`)
      .then((res) => setEmailTemplate(res.data[0]))
      .catch(() => {
        setSendEmailModal(false);
        notifyError(Vocabulary.getError);
      });
  }

  function saveOrderClickHandler() {
    if (order && order.statusId) {
      setSendEmailModal(true);
      getEmailTemplate(order.statusId);
    } else {
      saveOrder(false);
    }
  }

  function generatePvDocument() {
    if (!order?._id) return;
    setLoading(true);
    const token = localStorage.getItem("access_token");
    fetch(`${UrlEnum.repairOrders}/generatePv/${order._id}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (response.status !== 201 && response.status !== 200) {
          throw new Error("generate failed");
        }
        const contentDisposition = response.headers.get("Content-Disposition");
        let suggestedFilename = `PV_${order.orderNumber}.pdf`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
          if (filenameMatch && filenameMatch[1]) {
            suggestedFilename = filenameMatch[1];
          }
        }
        return response.blob().then((blob) => {
          const url = window.URL.createObjectURL(blob);
          setPvPreview({ open: true, url, filename: suggestedFilename });
        });
      })
      .catch(() => notifyError(Vocabulary.generatePvError))
      .finally(() => setLoading(false));
  }

  function closePvPreview() {
    if (pvPreview.url) {
      window.URL.revokeObjectURL(pvPreview.url);
    }
    setPvPreview({ open: false, url: null, filename: "" });
  }

  function downloadPvPreview() {
    if (!pvPreview.url) return;
    const a = document.createElement("a");
    a.href = pvPreview.url;
    a.download = pvPreview.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  return (
    <>
      {order && (
        <StatusOrder
          isOrder={true}
          status={order.status?.order}
          handleChangeStatus={handleChangeStatus}
          name="repairOrderStatus"
          styles={styles}
          statusType="repair"
        />
      )}

      <Grid container spacing={2} style={{ marginTop: 30, marginBottom: 120 }}>
        {/* Client info */}
        <Grid item xs={12}>
          <Paper elevation={2} style={{ padding: 20 }}>
            <Typography variant="h6" gutterBottom>
              {Vocabulary.clientInfo}
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label={Vocabulary.clientName}
                  value={order?.clientInfo?.name || ""}
                  onChange={(e) =>
                    handleChangeClientInfo("name", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={Vocabulary.email}
                  value={order?.clientInfo?.email || ""}
                  onChange={(e) =>
                    handleChangeClientInfo("email", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={Vocabulary.phone}
                  value={order?.clientInfo?.phoneNumber || ""}
                  onChange={(e) =>
                    handleChangeClientInfo("phoneNumber", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Client request (read-only, submitted via the public repair wizard) */}
        {order?.repair &&
          (order.repair.selectedRepairs ||
            order.repair.existingRepairDetails ||
            order.repair.otherProblemDescription ||
            order.repair.device) && (
            <Grid item xs={12}>
              <Paper elevation={2} style={{ padding: 20 }}>
                <Typography variant="h6" gutterBottom>
                  {Vocabulary.clientRequest}
                </Typography>
                <Divider style={{ marginBottom: 16 }} />
                <Grid container spacing={2}>
                  {order.repair.device && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">
                        {Vocabulary.requestedDevice}
                      </Typography>
                      <Typography variant="body2">
                        {order.repair.device.name} (
                        {order.repair.device.manufacturer})
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      {Vocabulary.requestedRepairs}
                    </Typography>
                    {(order.repair.selectedRepairs || []).length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        {Vocabulary.noRequestedRepairs}
                      </Typography>
                    ) : (
                      order.repair.selectedRepairs.map(
                        (r: any, i: number) => (
                          <Typography variant="body2" key={r._id || i}>
                            {r.repairName} — {r.totalPrice} RON
                          </Typography>
                        )
                      )
                    )}
                  </Grid>
                  {order.repair.existingRepairDetails && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">
                        {Vocabulary.existingRepairDetails}
                      </Typography>
                      <Typography variant="body2">
                        {order.repair.existingRepairDetails}
                      </Typography>
                    </Grid>
                  )}
                  {order.repair.otherProblemDescription && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2">
                        {Vocabulary.otherProblemDescription}
                      </Typography>
                      <Typography variant="body2">
                        {order.repair.otherProblemDescription}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          )}

        {/* Repair info */}
        <Grid item xs={12}>
          <Paper elevation={2} style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <Typography variant="h6">{Vocabulary.repairInfo}</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setOpenRepairModal(true)}
              >
                {Vocabulary.newRepair}
              </Button>
            </div>
            <Divider style={{ marginBottom: 16 }} />

            <Grid container spacing={2}>
              {/* Load template */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={deviceRepairs}
                  getOptionLabel={(r: any) =>
                    r.repairName
                      ? `${r.repairName} – ${r.deviceName || ""}`
                      : r.deviceName || ""
                  }
                  isOptionEqualToValue={(opt: any, val: any) =>
                    String(opt._id) === String(val._id)
                  }
                  value={null}
                  onChange={handleLoadRepairTemplate}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={Vocabulary.changeRepair}
                      size="small"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Device name + repair name */}
              <Grid item xs={12} md={3}>
                <TextField
                  label={Vocabulary.deviceName}
                  value={order?.repair?.deviceName || ""}
                  onChange={(e) =>
                    handleChangeRepairField("deviceName", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  label={Vocabulary.repairName}
                  value={order?.repair?.repairName || ""}
                  onChange={(e) =>
                    handleChangeRepairField("repairName", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Diagnosis */}
              <Grid item xs={12} md={12} lg={12}>
                <TextField
                  label={Vocabulary.reportedFaults}
                  value={order?.repair?.reportedFaults || ""}
                  onChange={(e) =>
                    handleChangeRepairField("reportedFaults", e.target.value)
                  }
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <TextField
                  label={Vocabulary.faultsCause}
                  value={order?.repair?.faultsCause || ""}
                  onChange={(e) =>
                    handleChangeRepairField("faultsCause", e.target.value)
                  }
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={12} lg={12}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    label={Vocabulary.estimatedRepairTime}
                    inputFormat="DD-MM-YYYY"
                    value={
                      order?.repair?.estimatedRepairTime
                        ? moment(order.repair.estimatedRepairTime)
                        : null
                    }
                    onChange={(value: any) =>
                      handleChangeRepairField(
                        "estimatedRepairTime",
                        value ? value.format("YYYY-MM-DDTHH:mm:ss") : null
                      )
                    }
                    renderInput={(params) => (
                      <TextField {...params} fullWidth size="small" />
                    )}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Spare parts */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  {Vocabulary.spareParts}
                </Typography>
                {(order?.repair?.spareParts || []).map(
                  (part: any, index: number) => (
                    <Grid
                      container
                      spacing={1}
                      key={part._key || `${part.name}-${part.price}`}
                      style={{ marginBottom: 8 }}
                      alignItems="center"
                    >
                      <Grid item xs={12} md={6}>
                        <TextField
                          label={Vocabulary.sparePart}
                          value={part.name || ""}
                          onChange={(e) =>
                            handleChangeSparePartField(
                              index,
                              "name",
                              e.target.value
                            )
                          }
                          fullWidth
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={9} md={5}>
                        <TextField
                          label={Vocabulary.price}
                          type="number"
                          value={part.price ?? ""}
                          onChange={(e) =>
                            handleChangeSparePartField(
                              index,
                              "price",
                              e.target.value
                            )
                          }
                          fullWidth
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                RON
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={3} md={1}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveSparePart(index)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  )
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddSparePart}
                  style={{ marginTop: 4 }}
                >
                  {Vocabulary.addSparePart}
                </Button>
              </Grid>

              {/* Labour price + total */}
              <Grid item xs={12} md={6}>
                <TextField
                  label={Vocabulary.labourPrice}
                  type="number"
                  value={order?.repair?.labourPrice ?? ""}
                  onChange={(e) =>
                    handleChangeRepairField(
                      "labourPrice",
                      Number(e.target.value)
                    )
                  }
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">RON</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={Vocabulary.totalRepairPrice}
                  value={
                    order?.repair?.totalPrice != null
                      ? `${order.repair.totalPrice} RON`
                      : "—"
                  }
                  InputProps={{ readOnly: true }}
                  fullWidth
                  size="small"
                />
              </Grid>

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  label={Vocabulary.repairNotes}
                  value={order?.repair?.notes || ""}
                  onChange={(e) =>
                    handleChangeRepairField("notes", e.target.value)
                  }
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Shipping */}
        <OrderShipping
          shipping={order?.shipping || null}
          fanCourierInfo={order?.fanCourierInfo || null}
          fanCourierInfoReturn={order?.fanCourierInfoReturn || null}
          order={order}
          handleChangeOrder={handleChangeOrder}
          handleChangeShipping={handleChangeShipping}
          saveOrder={saveOrder}
          fanCourierOverrides={{
            generateAWBUrl: `${UrlEnum.repairOrders}/generateAWB/${order?._id}`,
            buildDeleteAWBUrl: (awb: string) =>
              `${UrlEnum.repairOrders}/deleteAWB/${order?._id}/${awb}`,
            createFanOrderUrl: `${UrlEnum.repairOrders}/createFanOrder/${order?._id}`,
            buildDeleteOrderUrl: () =>
              `${UrlEnum.repairOrders}/deleteOrder/${order?._id}`,
          }}
        />
      </Grid>

      {/* Fixed save bar */}
      <div
         className={style.bottomBar}
      >
        <Button
          variant="contained"
          size="small"
          style={{ padding:10, color: theme.palette.whiteColor?.main }}
          onClick={saveOrderClickHandler}
        >
          <SaveIcon /> &nbsp;&nbsp; {Vocabulary.save}
        </Button>
        {order?._id && (
          <Button
            variant="outlined"
            size="small"
            onClick={generatePvDocument}
          >
            <DescriptionIcon /> &nbsp;&nbsp; {Vocabulary.generatePvDocument}
          </Button>
        )}
      </div>

      <DeviceRepairModal
        open={openRepairModal}
        data={null}
        onClose={handleRepairModalClose}
      />

      <SendEmailModal
        open={sendEmailModal}
        data={emailTemplate}
        onClose={() => {
          setSendEmailModal(false);
          setEmailTemplate(null);
        }}
        handleSendEmail={(value: boolean, body?: any) => {
          saveOrder(value, body);
          setSendEmailModal(false);
        }}
        title={""}
      />

      {pvPreview.open && (
        <Modal
          open={pvPreview.open}
          onClose={closePvPreview}
          title={Vocabulary.pvPreviewTitle}
          restModalProps={{ maxWidth: "md", fullWidth: true }}
        >
          <iframe
            src={pvPreview.url || ""}
            title={Vocabulary.pvPreviewTitle}
            style={{ width: "100%", height: "75vh", border: "none" }}
          />
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <Button
              variant="contained"
              style={{ color: theme.palette.whiteColor?.main }}
              onClick={downloadPvPreview}
            >
              <DownloadIcon /> &nbsp;&nbsp; {Vocabulary.downloadPdf}
            </Button>
          </div>
        </Modal>
      )}

      {loading && (
        <div className="loading">
          <CircularProgress color="primary" />
        </div>
      )}
    </>
  );
}
